// Dense month grid (Sunday start). Holiday lanes first, then stacked,
// non-overlapping multi-day trip bars laid out per week.
import { useMemo, useRef } from "react";
import { TABLE_TOP_CORNERS, useGridHeaderCorners } from "../../../v2/components/tableHeaderCorners.js";
import { T } from "../../../app/tokens.js";
import { tx, holidayStripe } from "./travelStyles.js";
import { buildMonthWeeks, purposeStyle, purposeLabel, travelerSummary, holidayLabel, fmtCivil } from "./travelModel.js";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const BAR = 24;
const GAP = 4;

export function TravelMonth({ month, zone, today, trips, holidays, showNames, holidayTripIds, gapTripIds, onOpenTrip }) {
  const headerRef = useRef(null);
  const viewportRef = useRef(null);
  useGridHeaderCorners(headerRef, viewportRef);
  const weeks = useMemo(() => buildMonthWeeks(month, trips, holidays, zone), [month, trips, holidays, zone]);
  return (
    <div data-testid="travel-month" role="grid" aria-label={`Travel calendar ${month}`} style={{ ...tx.card, ...TABLE_TOP_CORNERS, overflow: "hidden" }}>
      <style>{`
        .travel-ev { transition: filter .12s; }
        .travel-ev:hover, .travel-ev:focus-visible { filter: brightness(.97); outline: 2px solid ${T.travelFocusRing}; outline-offset: 0; }
        @media (max-width: 760px) { .travel-month-scroll { overflow-x: auto; } .travel-month-inner { min-width: 720px; } }
      `}</style>
      <div ref={viewportRef} className="travel-month-scroll" style={TABLE_TOP_CORNERS}><div className="travel-month-inner">
        <div ref={headerRef} role="row" style={{ ...TABLE_TOP_CORNERS, display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", background: T.travelDowBg }}>
          {DOW.map((d, i) => (
            <div role="columnheader" key={d} style={{ padding: "8px 10px", fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: T.mid, borderLeft: i ? `1px solid ${T.travelHair}` : 0 }}>{d}</div>
          ))}
        </div>
        {weeks.map((week) => {
          const height = Math.max(118, 34 + week.lanes * (BAR + GAP) + 8);
          return (
            <div role="row" key={week.weekStart} data-testid={`travel-week-${week.weekStart}`} style={{ position: "relative", borderTop: `1px solid ${T.travelHair}`, minHeight: height }}>
              <div style={{ position: "absolute", inset: 0, display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))" }}>
                {week.days.map((day, i) => {
                  const out = day.slice(0, 7) !== month;
                  const isToday = day === today;
                  const label = day.endsWith("-01") ? fmtCivil(day, { month: "long", day: "numeric" }) : String(Number(day.slice(8)));
                  return (
                    <div role="gridcell" key={day} data-testid={`travel-day-${day}`} aria-current={isToday ? "date" : undefined}
                      style={{ borderLeft: i ? `1px solid ${T.travelHair}` : 0, padding: "6px 8px", fontSize: T.fsSm, color: out ? T.light : T.dark, background: out ? T.travelOutBg : "transparent" }}>
                      <span style={isToday ? { background: T.blue, color: T.white, borderRadius: 6, padding: "0 6px", fontWeight: T.fwBold } : undefined}>{label}</span>
                      {isToday && <span style={tx.sr}> (today)</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ position: "relative", paddingTop: 30, paddingBottom: 8, display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", gridAutoRows: BAR, rowGap: GAP, pointerEvents: "none" }}>
                {week.items.map((item) => item.kind === "holiday" ? (
                  <div key={item.id} data-testid={`travel-holiday-${item.holiday.id}`} aria-label={`${item.holiday.scope === "ripple_wide" ? "Ripple-wide" : item.holiday.country} · ${item.holiday.name}`} title={`${holidayLabel(item.holiday)} · from Admin`}
                    style={{ gridColumn: `${item.colStart} / ${item.colEnd}`, gridRow: item.lane + 1, margin: "0 4px", borderRadius: 6, background: holidayStripe, color: T.travelHolidayFg, border: `1px solid ${T.travelHolidayBorder}`, fontSize: T.fsXs, fontWeight: T.fwSemibold, display: "flex", alignItems: "center", gap: 6, padding: "0 8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pointerEvents: "auto" }}>
                    {item.holiday.scope !== "ripple_wide" && <span style={tx.flag}>{item.holiday.country}</span>}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.holiday.scope === "ripple_wide" ? "Ripple-wide · " : " · "}{item.holiday.name || "Unnamed non-working day"}</span>
                  </div>
                ) : (
                  <TripBar key={`${item.id}-${week.weekStart}`} item={item} showNames={showNames} hasHoliday={holidayTripIds.has(item.trip.id)} gap={gapTripIds.has(item.trip.id)} onOpen={onOpenTrip} />
                ))}
              </div>
            </div>
          );
        })}
      </div></div>
    </div>
  );
}

function TripBar({ item, showNames, hasHoliday, gap, onOpen }) {
  const { trip } = item;
  const tone = purposeStyle(trip.purposeType);
  const who = travelerSummary(trip, showNames);
  return (
    <button type="button" className="travel-ev" data-testid={`travel-bar-${trip.id}`} onClick={() => onOpen(trip.id)}
      aria-label={`${trip.title}, ${purposeLabel(trip.purposeType)}, ${trip.destinationLabel}, ${who}${hasHoliday ? ", overlaps a non-working day" : ""}`}
      title={`${trip.title} · ${trip.destinationLabel} · ${who}`}
      style={{ gridColumn: `${item.colStart} / ${item.colEnd}`, gridRow: item.lane + 1, margin: "0 4px", minWidth: 0, height: 24, borderRadius: 6, background: tone.bg, color: tone.fg,
        border: gap ? `1.5px dashed ${T.travelGapBorder}` : `1px solid ${tone.border}`, fontSize: T.fsXs, fontWeight: T.fwSemibold, display: "flex", alignItems: "center", gap: 6, padding: "0 8px",
        whiteSpace: "nowrap", overflow: "hidden", cursor: "pointer", pointerEvents: "auto", fontFamily: "inherit", textAlign: "left" }}>
      <span style={tx.flag}>{trip.destinationCountry}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {trip.title} <span style={{ fontWeight: T.fwRegular, opacity: 0.8 }}>· {who}</span>
      </span>
      {hasHoliday && <span aria-hidden="true" data-testid={`travel-bar-holiday-${trip.id}`} style={{ marginLeft: "auto", fontWeight: T.fwBlack }} title="Overlaps a non-working day at the destination">⚑</span>}
    </button>
  );
}
